import React from "react";
import { DashAntd } from "../../../components/DashAntd";
import * as api from "../../../components/drilldown/drilldownApi";
import { GPanel } from "../parts";
import { FolderKanban, Layers, Clock, Zap, Pause, Play, Users } from "lucide-react";
import { Avatar } from "../../../components/primitives";
import { fmtMins } from "../../../components/classic/micro";
import { DrillFrame } from "./DrillFrame";
import useEscapeBack from "./useEscapeBack";
import {
  ProjectDigestStory, ProjectThemeView, ProjectPersonStory,
  useProjectStory, useProjectTheme, useSitParam,
  sittingInkMap, sitId, sitMinutes, honestFace,
} from "./ProjectStory";

/**
 * The project drill, framed as a zoom instrument. Each view owns its own
 * SSE stream (digest / theme / person), maps the REAL sitting start+end
 * times into ZoomInstrument segments, and shares one hot-segment state so
 * sitting rows and timeline blocks cross-light each other.
 *
 * The instrument only appears when every sitting falls on one day —
 * multi-day ranges have no honest minutes-since-midnight track, so it
 * hides itself.
 */

function ErrPanel({ t, text, pad = 28 }) {
  return <GPanel t={t}><div style={{ padding: pad, color: t.c.textTertiary }}>{text}</div></GPanel>;
}

function useSittingInk(baseSittings) {
  const [hotId, setHotId] = React.useState(null);
  const inkMap = React.useMemo(() => sittingInkMap(baseSittings), [baseSittings]);
  return { hotId, setHotId, inkMap };
}

/* ── digest: the whole project, instrument spans every known sitting ───── */
function DigestFrame({
  t, name, periodLabel, loading, error,
  contributors, trackedMinutes, activeMinutes, idleMinutes,
  crumbs, backTo, backLabel, subject, excludes,
  openMember, openTheme, projectId, range,
}) {
  const story = useProjectStory({ projectId: error ? null : projectId, range });
  const base = story.facts?.sittings?.length ? story.facts.sittings : story.sittings;
  const { inkMap } = useSittingInk(base);
  const peopleN = (contributors || []).length;
  const lead = [...(contributors || [])].sort((a, b) => (Number(b.totalHours) || 0) - (Number(a.totalHours) || 0))[0];
  const leadName = lead ? String(lead.firstName || "").trim() : "";
  const activePct = trackedMinutes > 0 ? Math.round((activeMinutes / trackedMinutes) * 100) : 0;
  const sentence = loading ? "Reading the project…" : (
    <>
      Tracked <b>{fmtMins(trackedMinutes)}</b>
      {periodLabel ? <> on <b>{periodLabel}</b></> : null}.
      {peopleN ? (
        <>
          {" "}{peopleN} contributor{peopleN === 1 ? "" : "s"}
          {leadName ? <> — most of it from <b>{leadName}</b></> : null}.
        </>
      ) : " No one on it in this window."}
      {story.themes.length ? <> {story.themes.length} piece{story.themes.length === 1 ? "" : "s"} of work.</> : null}
    </>
  );

  return (
    <DrillFrame
      t={t} title={name} icon={<FolderKanban size={22} strokeWidth={2.2} />} tone="violet" eyebrow="Project" subtitle={periodLabel}
      chips={[]}
      sentence={sentence}
      readings={[
        { icon: <Clock size={16} strokeWidth={2.2} />, value: loading ? "—" : fmtMins(trackedMinutes), label: periodLabel ? `tracked · ${periodLabel}` : "tracked", tone: "blue" },
        { icon: <Zap size={16} strokeWidth={2.2} />, value: fmtMins(activeMinutes), label: `${activePct}% of tracked was active`, tone: "teal" },
        { icon: <Pause size={16} strokeWidth={2.2} />, value: fmtMins(idleMinutes), label: idleMinutes > 0 ? "idle on this project" : "no idle on this project", tone: "gold" },
      ]}
      crumbs={crumbs} backTo={backTo} backLabel={backLabel}
      subject={[]} excludes={excludes}
    >
      <DashAntd>
        {error ? (
          <ErrPanel t={t} text={error} />
        ) : (
          <ProjectDigestStory
            t={t}
            projectId={projectId}
            range={range}
            name={name}
            periodLabel={periodLabel}
            contributors={contributors}
            trackedMinutes={trackedMinutes}
            activeMinutes={activeMinutes}
            idleMinutes={idleMinutes}
            openMember={openMember}
            openTheme={openTheme}
            story={story}
            loading={loading}
            inkMap={inkMap}
          />
        )}
      </DashAntd>
    </DrillFrame>
  );
}

/* ── theme: one piece of work ─────────────────────────────────────────── */
function ThemeFrame({
  t, name, themeTitle, periodLabel, error,
  crumbs, backTo, backLabel, excludes,
  openMember, onBack, projectId, range,
}) {
  const theme = useProjectTheme({ projectId: !themeTitle || error ? null : projectId, range, themeTitle });
  const { sitKey, closeSit } = useSitParam();
  const base = theme.facts?.sittings || [];
  const { inkMap } = useSittingInk(base);
  const sit = sitKey ? base.find((s) => sitId(s) === sitKey) || null : null;

  useEscapeBack(React.useCallback(() => {
    if (sitKey) closeSit(); else onBack();
  }, [sitKey, closeSit, onBack]));

  const sitsN = base.length;
  const mins = base.reduce((n, s) => n + sitMinutes(s), 0);
  const people = theme.facts?.people || [];
  const peopleN = people.length || new Set(base.map((s) => s.userId)).size;
  const lead = [...people].sort((a, b) => (Number(b.minutes) || 0) - (Number(a.minutes) || 0))[0];
  const sitFace = sit ? honestFace(sit) : null;
  const sitMins = sitFace?.mins || 0;
  const sitTitle = String(sit?.what || sit?.title || "").replace(/\s+and\s+\d+\s+more\s+pages?$/i, "").trim();

  return (
    <DrillFrame
      t={t}
      title={sit ? (sitTitle || "This sitting") : (themeTitle || "This piece of work")}
      icon={<Layers size={22} strokeWidth={2.2} />}
      tone="violet"
      eyebrow={sit ? "Project · one sitting" : "Project · one piece of work"}
      chips={[]}
      sentence={sit ? (
        <>
          {sitFace?.range ? <>From <b>{sitFace.range.split(" → ")[0]}</b> to <b>{sitFace.range.split(" → ")[1]}</b>{sit.day ? <> on <b>{prettyIso(sit.day)}</b></> : null}. </> : (sit.day ? <>On <b>{prettyIso(sit.day)}</b>. </> : null)}
          {sit.name ? <> {String(sit.name).split(" ")[0]} on <b>{name}</b>.</> : <> On <b>{name}</b>.</>}
        </>
      ) : theme.running && !headlineReady(theme) ? "Reading this piece of work…" : (
        <>
          <b>{themeTitle || "This piece"}</b> on <b>{name}</b>
          {periodLabel ? <> on <b>{periodLabel}</b></> : null}.
          {peopleN ? (
            <>
              {" "}{peopleN} {peopleN === 1 ? "person" : "people"}
              {lead?.name ? <> — most of it from <b>{String(lead.name).split(" ")[0]}</b></> : null}.
            </>
          ) : " No one named on it yet."}
          {sitsN ? <> {sitsN} sitting{sitsN === 1 ? "" : "s"}.</> : null}
        </>
      )}
      readings={sit ? [
        sitMins ? { icon: <Clock size={16} strokeWidth={2.2} />, value: fmtMins(sitMins), label: "this sitting", tone: "blue" } : null,
        sitFace?.range ? { icon: <Play size={16} strokeWidth={2.2} />, value: sitFace.range.split(" → ")[0], label: "started", tone: "teal" } : null,
        sitFace?.range ? { icon: <Pause size={16} strokeWidth={2.2} />, value: sitFace.range.split(" → ")[1], label: "ended", tone: "gold" } : null,
      ].filter(Boolean) : [
        { icon: <Clock size={16} strokeWidth={2.2} />, value: theme.running && !mins ? "—" : fmtMins(mins), label: "this piece", tone: "blue" },
        { icon: <Users size={16} strokeWidth={2.2} />, value: String(peopleN), label: peopleN === 1 ? "person on it" : "people on it", tone: "teal" },
        { icon: <Layers size={16} strokeWidth={2.2} />, value: String(sitsN), label: sitsN === 1 ? "sitting" : "sittings", tone: "violet" },
      ]}
      subtitle={periodLabel || name}
      crumbs={crumbs} backTo={backTo} backLabel={backLabel}
      subject={[]} excludes={excludes}
    >
      <DashAntd>
        {error ? (
          <ErrPanel t={t} text={error} />
        ) : themeTitle ? (
          <ProjectThemeView
            t={t}
            projectId={projectId}
            range={range}
            themeTitle={themeTitle}
            name={name}
            openMember={openMember}
            onBack={onBack}
            theme={theme}
            inkMap={inkMap}
          />
        ) : (
          <ErrPanel t={t} text="Open a piece of work from the project page." pad={24} />
        )}
      </DashAntd>
    </DrillFrame>
  );
}

function headlineReady(theme) {
  return Boolean(theme?.headline || theme?.facts || theme?.empty || theme?.error);
}

function prettyIso(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!m) return String(iso || "");
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/* ── person: one contributor's sittings on this project ────────────────── */
function PersonFrame({
  t, name, periodLabel, loading, error, contributor,
  crumbs, backTo, backLabel, subject, excludes,
  openPerson, onBack, projectId, range,
}) {
  const userId = contributor ? (contributor.userId || contributor.id) : null;
  const story = useProjectStory({ projectId: !contributor || error ? null : projectId, range, userId });
  const { sitKey, closeSit } = useSitParam();
  const base = story.facts?.sittings?.length ? story.facts.sittings : story.sittings;
  const { hotId, setHotId, inkMap } = useSittingInk(base);
  const sit = sitKey ? (base || []).find((s) => `${s.userId}.${s.startMs}` === sitKey) || null : null;

  useEscapeBack(React.useCallback(() => {
    if (sitKey) closeSit(); else onBack();
  }, [sitKey, closeSit, onBack]));

  const personName = contributor ? api.memberName(contributor) : "Contributor";
  const first = personName.split(" ")[0];
  const onMins = Math.round((Number(contributor?.totalHours) || 0) * 60);
  const sitsN = (base || []).length;
  const piecesN = (story.themes || []).length;
  const sitFace = sit ? honestFace(sit) : null;
  const sitMins = sitFace?.mins || 0;

  return (
    <DrillFrame
      t={t}
      title={sit ? (sit.what || sit.title || "This sitting") : personName}
      icon={sit
        ? <Layers size={22} strokeWidth={2.2} />
        : (contributor ? <Avatar user={contributor} name={personName} size={50} /> : null)}
      tone="blue" eyebrow={sit ? `${first} · on ${name}` : `On ${name}`}
      chips={[]}
      sentence={sit ? (
        <>
          {sitFace?.range ? <>From <b>{sitFace.range.split(" → ")[0]}</b> to <b>{sitFace.range.split(" → ")[1]}</b>{sit.day ? <> on <b>{prettyIso(sit.day)}</b></> : null}. </> : (sit.day ? <>On <b>{prettyIso(sit.day)}</b>. </> : null)}
          On <b>{name}</b>.
        </>
      ) : loading ? "Reading their work…" : (
        <>
          <b>{first}</b> spent <b>{fmtMins(onMins)}</b> on <b>{name}</b>
          {periodLabel ? <> on <b>{periodLabel}</b></> : null}.
          {sitsN ? <> {sitsN} sitting{sitsN === 1 ? "" : "s"}</> : " No sitting we can write up yet"}
          {piecesN ? <> · {piecesN} piece{piecesN === 1 ? "" : "s"} of work.</> : "."}
        </>
      )}
      readings={sit ? [
        sitMins ? { icon: <Clock size={16} strokeWidth={2.2} />, value: fmtMins(sitMins), label: "this sitting", tone: "blue" } : null,
        sitFace?.range ? { icon: <Play size={16} strokeWidth={2.2} />, value: sitFace.range.split(" → ")[0], label: "started", tone: "teal" } : null,
        sitFace?.range ? { icon: <Pause size={16} strokeWidth={2.2} />, value: sitFace.range.split(" → ")[1], label: "ended", tone: "gold" } : null,
      ].filter(Boolean) : [
        { icon: <Clock size={16} strokeWidth={2.2} />, value: loading ? "—" : fmtMins(onMins), label: `on ${name}`, tone: "blue" },
        { icon: <Layers size={16} strokeWidth={2.2} />, value: String(sitsN), label: sitsN === 1 ? "sitting" : "sittings", tone: "teal" },
        { icon: <FolderKanban size={16} strokeWidth={2.2} />, value: String(piecesN), label: piecesN === 1 ? "piece of work" : "pieces of work", tone: "gold" },
      ]}
      subtitle={periodLabel}
      crumbs={crumbs} backTo={backTo} backLabel={backLabel}
      subject={[]} excludes={excludes}
    >
      <DashAntd>
        {error ? (
          <ErrPanel t={t} text={error} />
        ) : contributor ? (
          <ProjectPersonStory
            t={t}
            projectId={projectId}
            range={range}
            name={name}
            contributor={contributor}
            openPerson={openPerson}
            onBack={onBack}
            story={story}
            inkMap={inkMap}
            hotId={hotId}
            onHot={setHotId}
          />
        ) : loading ? null : (
          <ErrPanel t={t} text="That person has no time on this project in this window." pad={24} />
        )}
      </DashAntd>
    </DrillFrame>
  );
}

export function GraphicalProject(props) {
  if (props.view === "member") return <PersonFrame {...props} />;
  if (props.view === "takeaway") return <ThemeFrame {...props} />;
  return <DigestFrame {...props} />;
}

export default GraphicalProject;
